import React, { useState } from 'react';
import AppBanner from './AppBanner';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

const StorePage = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [plan, setPlan] = useState('monthly'); // 'monthly' or 'yearly'
  const [loading, setLoading] = useState(false);

  // Real logout handler
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      alert('Failed to log out: ' + error.message);
    } else {
      window.location.href = '/login';
    }
  };

  // Stripe checkout integration
  const handleUpgrade = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        alert(t('storeMustBeLoggedIn'));
        setLoading(false);
        return;
      }
      const res = await fetch(`${backendUrl}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, plan })
      });
      const data = await res.json();
      setLoading(false);
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(t('storeFailedToStartCheckout', { error: data.error || 'Unknown error' }));
      }
    } catch (err) {
      setLoading(false);
      alert(t('storeFailedToStartCheckout', { error: err.message }));
    }
  };

  return (
    <>
      <AppBanner onLogout={handleLogout} />
      <div style={{ maxWidth: 520, margin: '60px auto', padding: 32, background: '#fff', borderRadius: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.09)' }} dir={isRTL ? 'rtl' : 'ltr'}>
        <h1 style={{ textAlign: 'center' }}>{t('storeUpgradeTitle')}</h1>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 24 }}>
          <button
            className='btn'
            style={{
              background: plan === 'monthly' ? 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)' : '#f1f8e9',
              color: plan === 'monthly' ? '#fff' : '#386641',
              border: plan === 'monthly' ? 'none' : '1px solid #b7e4c7',
              fontWeight: 600,
              fontSize: '1.1rem',
              borderRadius: 8,
              padding: '12px 28px',
              cursor: 'pointer',
              boxShadow: plan === 'monthly' ? '0 2px 8px rgba(56,102,65,0.10)' : 'none',
              transition: 'all 0.15s',
            }}
            onClick={() => setPlan('monthly')}
            disabled={loading}
          >
            {t('storeMonthlyPrice')}
          </button>
          <button
            className='btn'
            style={{
              background: plan === 'yearly' ? 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)' : '#f1f8e9',
              color: plan === 'yearly' ? '#fff' : '#386641',
              border: plan === 'yearly' ? 'none' : '1px solid #b7e4c7',
              fontWeight: 600,
              fontSize: '1.1rem',
              borderRadius: 8,
              padding: '12px 28px',
              cursor: 'pointer',
              boxShadow: plan === 'yearly' ? '0 2px 8px rgba(56,102,65,0.10)' : 'none',
              transition: 'all 0.15s',
            }}
            onClick={() => setPlan('yearly')}
            disabled={loading}
          >
            {t('storeYearlyPrice')}
          </button>
        </div>
        <div style={{
          background: '#f1f8e9',
          color: '#386641',
          borderRadius: 8,
          padding: '16px 20px',
          margin: '0 0 24px 0',
          fontSize: '1.08rem',
          fontWeight: 500,
          border: '1px solid #b7e4c7',
          boxShadow: '0 1px 4px rgba(56,102,65,0.06)'
        }}>
          <ul style={{ margin: 0, paddingLeft: 22, color: '#386641', fontWeight: 400, fontSize: '1.02rem', textAlign: isRTL ? 'right' : 'left' }}>
            <li>{t('storeFeature1').replace('AI', t('aiAbbreviation'))}</li>
            <li>{t('storeFeature2').replace('AI', t('aiAbbreviation'))}</li>
            <li>{t('storeFeature3')}</li>
            <li>{t('storeFeature5')}</li>
          </ul>
        </div>
        <div style={{
          background: '#e9f5e1',
          border: '1px solid #b7e4c7',
          borderRadius: 8,
          padding: '14px 18px',
          margin: '0 0 28px 0',
          fontSize: '1.04rem',
          color: '#386641',
          fontWeight: 500,
          boxShadow: '0 1px 4px rgba(56,102,65,0.06)'
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('storeDailyLimitsTitle')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1.01rem', direction: isRTL ? 'rtl' : 'ltr' }}>
            <thead>
              <tr>
                <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '4px 0', fontWeight: 500, color: '#386641', borderBottom: '1px solid #b7e4c7' }}></th>
                <th style={{ textAlign: 'center', padding: '4px 0', fontWeight: 500, color: '#386641', borderBottom: '1px solid #b7e4c7' }}>{t('storeFree')}</th>
                <th style={{ textAlign: 'center', padding: '4px 0', fontWeight: 500, color: '#386641', borderBottom: '1px solid #b7e4c7' }}>{t('storePremium')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '6px 0' }}>{t('storeWordsWithAI')}</td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>10</td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>100</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0' }}>{t('storeAIConversations')}</td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>3</td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>20</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ textAlign: 'center', marginBottom: 16, fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
          {t('storeRecurringNotice').split('[PROFILE_PAGE]').map((part, index, array) => (
            <React.Fragment key={index}>
              {part}
              {index < array.length - 1 && (
                <Link to="/profile" style={{ color: '#56ab2f', fontWeight: 600, textDecoration: 'none' }}>
                  {t('profilePage')}
                </Link>
              )}
            </React.Fragment>
          ))}
        </div>
        <div style={{ textAlign: 'center', margin: '16px 0 18px 0' }}>
          <button
            className='btn btn-primary'
            style={{ fontSize: '1.2rem', padding: '14px 38px', borderRadius: 8, fontWeight: 600, background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)', border: 'none', color: '#fff', boxShadow: '0 2px 8px rgba(56,102,65,0.10)' }}
            onClick={handleUpgrade}
            disabled={loading}
          >
            {loading ? t('storeRedirecting') : plan === 'monthly' ? t('storeUpgradeMonthly') : t('storeUpgradeYearly')}
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button className='btn' style={{ color: '#386641', background: 'none', border: 'none', fontWeight: 500, fontSize: '1.05rem', cursor: 'pointer' }} onClick={() => navigate('/')}>{t('storeBackToDashboard')}</button>
        </div>
      </div>
    </>
  );
};

export default StorePage; 